import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, AlertTriangle, Clock, CreditCard, Loader2, Eye, EyeOff, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import { Button } from './ui/button';

/**
 * TrialBanner — outcome-focused copy emphasising VISIBILITY (people can find
 * you right now), LOSS (your events will be hidden), and EASE (go live again
 * in seconds). Variants:
 *
 *   trial,  >7 days,  no card     → calm green, focus on visibility
 *   trial,  ≤7 days,  no card     → amber, urgency about the loss
 *   trial,  ≤2 days,  no card     → red, very strong loss messaging
 *   trial,  has card              → emerald confirmation chip
 *   paused                        → red "events hidden right now" + churn dropdown
 *   active                        → renders nothing
 */
const TrialBanner = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const load = useCallback(async () => {
    if (!user || user.role !== 'business') { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/stripe/trial-status`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setStatus(r.data);
    } catch (e) {
      // Silent — the banner just doesn't render
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const upgrade = async () => {
    setUpgrading(true);
    try {
      const r = await axios.post(
        `${API}/stripe/trial-upgrade-checkout`,
        { originUrl: window.location.origin },
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      if (r.data?.checkoutUrl) {
        window.location.href = r.data.checkoutUrl;
      } else {
        toast.error('Could not create checkout session');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start upgrade');
    } finally {
      setUpgrading(false);
    }
  };

  const submitFeedback = async () => {
    if (!feedbackReason) return;
    setSubmittingFeedback(true);
    try {
      await axios.post(
        `${API}/stripe/trial-feedback`,
        { reason: feedbackReason, note: feedbackNote },
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      setFeedbackSent(true);
      toast.success(t('trial.feedbackThanks', 'Thanks — we appreciate it.'));
    } catch (e) {
      toast.error(t('trial.feedbackError', 'Could not send feedback right now.'));
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading || !status) return null;

  const { subscriptionStatus, daysRemaining, hasPaymentMethod } = status;
  if (subscriptionStatus === 'active') return null;

  // -------- Paused — biggest, reddest banner with churn-reason capture --------
  if (subscriptionStatus === 'paused') {
    return (
      <div
        className="bg-red-50 border-2 border-red-300 rounded-xl p-5 mb-6 shadow-sm"
        data-testid="trial-banner-paused"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <EyeOff className="h-6 w-6 text-red-700" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-red-900 mb-1">
              {t('trial.pausedTitleV2', 'Your events and offers are hidden right now')}
            </h3>
            <p className="text-sm text-red-800 mb-3">
              {t('trial.pausedBodyV2', 'Customers are searching today and they can\'t see your events or offers. Add a payment method to go live again in seconds — your listing, photos, and reviews are all preserved.')}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={upgrade}
                disabled={upgrading}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="trial-upgrade-paused-btn"
              >
                {upgrading
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />…</>
                  : <><Zap className="h-4 w-4 mr-2" />{t('trial.goLiveAgain', 'Go live again')}</>}
              </Button>
              {!feedbackSent && !feedbackOpen && (
                <button
                  onClick={() => setFeedbackOpen(true)}
                  className="text-sm text-red-800 underline hover:text-red-900"
                  data-testid="trial-feedback-open"
                >
                  {t('trial.feedbackOpenLink', 'Not now — tell us why')}
                </button>
              )}
            </div>

            {feedbackOpen && !feedbackSent && (
              <div className="mt-4 bg-white rounded-lg border border-red-200 p-4" data-testid="trial-feedback-form">
                <p className="text-sm font-medium text-gray-900 mb-2">
                  {t('trial.feedbackPrompt', 'Mind sharing why? It helps us improve.')}
                </p>
                <div className="space-y-2 mb-3">
                  {[
                    { k: 'not_ready', l: t('trial.reasonNotReady', 'Not ready yet') },
                    { k: 'price_too_high', l: t('trial.reasonPriceTooHigh', 'Price too high') },
                    { k: 'prefer_monthly', l: t('trial.reasonPreferMonthly', 'Prefer to pay monthly') },
                    { k: 'other', l: t('trial.reasonOther', 'Other') },
                  ].map((opt) => (
                    <label
                      key={opt.k}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                        feedbackReason === opt.k ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      data-testid={`trial-feedback-reason-${opt.k}`}
                    >
                      <input
                        type="radio"
                        name="trial-reason"
                        checked={feedbackReason === opt.k}
                        onChange={() => setFeedbackReason(opt.k)}
                      />
                      <span>{opt.l}</span>
                    </label>
                  ))}
                </div>
                {feedbackReason === 'other' && (
                  <textarea
                    value={feedbackNote}
                    onChange={(e) => setFeedbackNote(e.target.value.slice(0, 500))}
                    placeholder={t('trial.feedbackNotePlaceholder', 'Anything you want to add (optional)')}
                    rows={2}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none mb-3"
                    data-testid="trial-feedback-note"
                  />
                )}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={submitFeedback}
                    disabled={!feedbackReason || submittingFeedback}
                    className="bg-gray-800 hover:bg-gray-900 text-white"
                    data-testid="trial-feedback-submit"
                  >
                    {submittingFeedback ? <Loader2 className="h-4 w-4 animate-spin" /> : t('trial.feedbackSend', 'Send')}
                  </Button>
                  <button
                    onClick={() => { setFeedbackOpen(false); setFeedbackReason(''); setFeedbackNote(''); }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                    data-testid="trial-feedback-cancel"
                  >
                    {t('cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            )}

            {feedbackSent && (
              <p className="mt-3 text-sm text-emerald-700 font-medium" data-testid="trial-feedback-thanks">
                ✓ {t('trial.feedbackThanks', 'Thanks — we appreciate it.')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // -------- Trial — vary urgency by daysRemaining --------
  if (subscriptionStatus !== 'trial') return null;
  const days = Math.max(daysRemaining ?? 0, 0);

  // Already added a card during trial — small confirmation chip only
  if (hasPaymentMethod) {
    return (
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex items-center gap-3"
        data-testid="trial-banner-card-added"
      >
        <Sparkles className="h-5 w-5 text-emerald-600 flex-shrink-0" />
        <p className="text-sm text-emerald-900">
          {t('trial.cardAddedBody', 'Card added — you\'ll be billed automatically when the trial ends in {{days}} day(s). No further action needed.', { days })}
        </p>
      </div>
    );
  }

  let tone = 'green';
  if (days <= 2) tone = 'red';
  else if (days <= 7) tone = 'amber';

  const styles = {
    green: { bg: 'bg-emerald-50', border: 'border-emerald-300', icon: 'text-emerald-700', iconBg: 'bg-emerald-100', heading: 'text-emerald-900', body: 'text-emerald-800', btn: 'bg-emerald-600 hover:bg-emerald-700' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-300', icon: 'text-amber-700', iconBg: 'bg-amber-100', heading: 'text-amber-900', body: 'text-amber-800', btn: 'bg-amber-600 hover:bg-amber-700' },
    red:   { bg: 'bg-red-50',   border: 'border-red-300',   icon: 'text-red-700',   iconBg: 'bg-red-100',   heading: 'text-red-900',   body: 'text-red-800',   btn: 'bg-red-600 hover:bg-red-700' },
  }[tone];

  // Outcome-focused headlines: lead with VISIBILITY (calm), LOSS (urgent)
  const heading = (
    days <= 0 ? t('trial.endingTodayTitleV2', 'Your last day live — events go dark tomorrow')
      : days === 1 ? t('trial.endingTomorrowTitleV2', 'Tomorrow your events disappear from search')
      : days <= 2 ? t('trial.urgentTitleV2', 'You\'ll vanish from Discover in {{days}} days', { days })
      : days <= 7 ? t('trial.amberTitleV2', '{{days}} days left — keep your visibility', { days })
      : t('trial.calmTitleV2', 'You\'re live on Find-2-Day right now', { days })
  );

  // Outcome-focused body: people can find you · loss · ease
  const body = (
    days <= 7
      ? t('trial.urgentBodyV2', 'Locals are searching today and you\'re showing up. Add a payment method now and you stay live with no break in visibility — your events, offers and listing all keep working as if nothing changed.')
      : t('trial.calmBodyV2', 'People can find you in Discover, your events show up in search, and your offers are reaching customers. {{days}} days left in your trial — add a payment method any time and you\'ll never see a service interruption.', { days })
  );

  return (
    <div
      className={`${styles.bg} ${styles.border} border-2 rounded-xl p-5 mb-6 shadow-sm`}
      data-testid={`trial-banner-${tone}`}
    >
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-12 h-12 rounded-full ${styles.iconBg} flex items-center justify-center`}>
          {tone === 'green' ? <Eye className={`h-6 w-6 ${styles.icon}`} />
            : tone === 'amber' ? <Clock className={`h-6 w-6 ${styles.icon}`} />
            : <AlertTriangle className={`h-6 w-6 ${styles.icon}`} />}
        </div>
        <div className="flex-1">
          <h3 className={`text-lg font-bold ${styles.heading} mb-1`}>{heading}</h3>
          <p className={`text-sm ${styles.body} mb-3`}>{body}</p>
          <Button
            onClick={upgrade}
            disabled={upgrading}
            className={`${styles.btn} text-white`}
            data-testid="trial-upgrade-btn"
          >
            {upgrading
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />…</>
              : tone === 'green'
                ? <><CreditCard className="h-4 w-4 mr-2" />{t('trial.lockInVisibility', 'Lock in your visibility')}</>
                : <><CreditCard className="h-4 w-4 mr-2" />{t('trial.stayLive', 'Stay live — add payment method')}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
